import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useUser } from "@clerk/react";
import { load } from "@cashfreepayments/cashfree-js";
import { toast } from "sonner";
import { publicApi, useApi, errMsg } from "@/lib/api";
import { RequireAuth } from "@/components/site/Shared";
import { KycPanel } from "@/components/kyc/KycPanel";
import { DriverForm } from "@/components/checkout/DriverForm";
import { CheckoutSummary } from "@/components/checkout/CheckoutSummary";
import { CouponBox } from "@/components/checkout/CouponBox";
import { PhoneOtp, usePhoneStatus } from "@/components/checkout/PhoneOtp";

export async function openCashfree(sessionId, mode) {
  const cf = await load({ mode: mode === "production" ? "production" : "sandbox" });
  await cf.checkout({ paymentSessionId: sessionId, redirectTarget: "_self" });
}

function useCheckoutData(carId, pickup, ret, protection, withDriver, setError) {
  const [car, setCar] = useState(null);
  const [quote, setQuote] = useState(null);
  const [payCfg, setPayCfg] = useState(null);
  useEffect(() => {
    publicApi.get(`/cars/${carId}`).then((r) => setCar(r.data));
    publicApi.post("/quote", { car_id: carId, pickup_at: pickup, return_at: ret, protection, with_driver: withDriver }).then((r) => setQuote(r.data)).catch((e) => setError(errMsg(e)));
    publicApi.get("/payments/config").then((r) => setPayCfg(r.data)).catch(() => setPayCfg({ enabled: false }));
  }, [carId, pickup, ret, protection, withDriver, setError]);
  return { car, quote, payCfg };
}

function CheckoutInner() {
  const { carId } = useParams();
  const [params] = useSearchParams();
  const { user } = useUser();
  const api = useApi();
  const pickup = params.get("pickup");
  const ret = params.get("return");
  const protection = params.get("protection") === "1";
  const withDriver = params.get("driver") === "1";
  const [error, setError] = useState("");
  const { car, quote, payCfg } = useCheckoutData(carId, pickup, ret, protection, withDriver, setError);
  const [form, setForm] = useState({ name: "", phone: "", location: params.get("airport") === "1" ? "Kempegowda International Airport (BLR) — Flight no: " : "" });
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [kycStatus, setKycStatus] = useState(null);
  const kycOk = ["PENDING", "APPROVED"].includes(kycStatus);
  const [couponQuote, setCouponQuote] = useState(null);
  const [phoneStatus, setPhoneStatus] = usePhoneStatus();
  const phoneOk = !phoneStatus?.enabled || phoneStatus.verified_phone === form.phone.replace(/\D/g, "").slice(-10);
  const shownQuote = couponQuote || quote;
  const quoteParams = { car_id: carId, pickup_at: pickup, return_at: ret, protection, with_driver: withDriver };

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, name: f.name || user.fullName || "" }));
  }, [user]);

  const pay = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const r = await api.post("/bookings", {
        car_id: carId, pickup_at: pickup, return_at: ret, protection, with_driver: withDriver,
        customer_name: form.name, customer_phone: form.phone, pickup_location: form.location, agree_terms: agree,
        coupon_code: couponQuote?.coupon_code || "",
      });
      if (couponQuote?.coupon_code === localStorage.getItem("hc_ref")) localStorage.removeItem("hc_ref");
      await openCashfree(r.data.payment_session_id, r.data.mode);
    } catch (err) {
      const m = errMsg(err);
      setError(m);
      toast.error(m);
      setBusy(false);
    }
  };

  if (!car) return <div className="container-x py-24 text-neutral-500">Loading…</div>;

  return (
    <div className="container-x py-12" data-testid="checkout-page">
      <p className="label-eyebrow">Checkout</p>
      <h1 className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl">Confirm your booking</h1>
      {withDriver && <p className="mt-3 inline-flex rounded-full bg-orange-50 px-4 py-1.5 text-sm font-medium text-[#c2410c]" data-testid="checkout-with-driver">With Driver · a professional driver will be assigned before pickup</p>}
      <div className="mt-10 grid gap-10 lg:grid-cols-[1.4fr_1fr]">
        <div className="space-y-6">
          <KycPanel onStatus={setKycStatus} />
          <DriverForm car={car} form={form} setForm={setForm} agree={agree} setAgree={setAgree} onSubmit={pay} busy={busy} quote={shownQuote}
            canPay={!!quote && !!payCfg?.enabled && kycOk && phoneOk} notices={{ kycStatus, kycOk, payCfg, error }}
            phoneSlot={<PhoneOtp phone={form.phone} status={phoneStatus} setStatus={setPhoneStatus} />}
            couponSlot={quote && <CouponBox params={quoteParams} applied={couponQuote} onApplied={setCouponQuote} />} />
        </div>
        <CheckoutSummary car={car} pickup={pickup} ret={ret} quote={shownQuote} />
      </div>
    </div>
  );
}

export default function Checkout() {
  return <RequireAuth><CheckoutInner /></RequireAuth>;
}
